// ─── LOGIKA FINANSOWA ────────────────────────────────────────────────────────
// Opłacone prywatnie NIE zmniejsza salda od razu
// Saldo zmniejsza się dopiero przy faktycznej wypłacie ze zbiórki
// Zbiórki zwiększają saldo o faktycznie wpłaconą kwotę current_amount

import {
  getActivePlannedExpenses,
  getKnownPlannedAmount,
} from "./plannedExpenses";

function parseNonNegativeDecimal(value) {
  if (value === "" || value === null || value === undefined) return null;

  const normalized = String(value).trim().replace(",", ".");
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return null;

  const [whole = "0", fraction = ""] = normalized.split(".");
  return {
    numerator: BigInt(`${whole || "0"}${fraction}`),
    denominator: 10n ** BigInt(fraction.length),
  };
}

function divideAndRound(numerator, denominator) {
  return (numerator + denominator / 2n) / denominator;
}

export function moneyToCents(value) {
  const parsed = parseNonNegativeDecimal(value);
  if (!parsed) return null;

  const cents = divideAndRound(parsed.numerator * 100n, parsed.denominator);
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(cents);
}

function signedMoneyToCents(value) {
  const normalized = String(value ?? 0).trim().replace(",", ".");
  if (!normalized.startsWith("-")) return moneyToCents(normalized);
  const absoluteCents = moneyToCents(normalized.slice(1));
  return absoluteCents === null ? null : -absoluteCents;
}

export function centsToMoney(cents) {
  if (!Number.isSafeInteger(cents)) return null;
  return cents / 100;
}

export function formatMoneyInput(value) {
  const cents = moneyToCents(value);
  return cents === null ? "" : (cents / 100).toFixed(2);
}

export function sumMoney(values = []) {
  const totalCents = values.reduce(
    (sum, value) => sum + (moneyToCents(value) ?? 0),
    0
  );
  return centsToMoney(totalCents) ?? 0;
}

export function calculateExpenseAmounts({
  quantity,
  unitPrice,
  deliveryCost,
  totalValue,
  studentCount,
} = {}) {
  const parsedQuantity = parseNonNegativeDecimal(quantity);
  const unitPriceCents = moneyToCents(unitPrice);
  const hasItemizedValues = parsedQuantity !== null && unitPriceCents !== null;

  let totalCents = moneyToCents(totalValue);
  if (hasItemizedValues) {
    const itemsCents = divideAndRound(
      BigInt(unitPriceCents) * parsedQuantity.numerator,
      parsedQuantity.denominator
    );
    totalCents = Number(itemsCents) + (moneyToCents(deliveryCost) ?? 0);
  }

  const parsedStudentCount = Number(studentCount);
  const hasStudentCount =
    Number.isSafeInteger(parsedStudentCount) && parsedStudentCount > 0;
  const costPerChildCents =
    totalCents !== null && hasStudentCount
      ? Number(divideAndRound(BigInt(totalCents), BigInt(parsedStudentCount)))
      : null;

  return {
    hasItemizedValues,
    hasStudentCount,
    totalValue: totalCents === null ? null : centsToMoney(totalCents),
    payoutAmount: totalCents === null ? null : centsToMoney(totalCents),
    costPerChild:
      costPerChildCents === null ? null : centsToMoney(costPerChildCents),
    studentCountSnapshot: hasStudentCount ? parsedStudentCount : null,
  };
}

export function getExpenseAmounts(expense = {}, fallbackStudentCount = null) {
  const calculated = calculateExpenseAmounts({
    quantity: expense.quantity,
    unitPrice: expense.unit_price,
    deliveryCost: expense.delivery_cost,
    totalValue: expense.total_value,
    studentCount:
      expense.student_count_snapshot !== undefined
        ? expense.student_count_snapshot
        : fallbackStudentCount,
  });

  return {
    ...calculated,
    costPerChild:
      calculated.costPerChild ?? centsToMoney(moneyToCents(expense.cost_per_child)),
  };
}

export function getExpenseTotal(expense = {}) {
  return getExpenseAmounts(expense).totalValue ?? 0;
}

export function getExpensePayout(expense = {}) {
  return getExpenseAmounts(expense).payoutAmount ?? 0;
}

export function calcBalance(expenses = [], settings = {}, collections = []) {
  const startBalanceCents =
    signedMoneyToCents(
      settings?.opening_balance ?? settings?.start_balance ?? 0
    ) ?? 0;

  const totalPayoutCents = expenses
    .filter((expense) => expense.status === "Wypłacone")
    .reduce(
      (sum, expense) => sum + (moneyToCents(getExpensePayout(expense)) ?? 0),
      0
    );

  const totalCollectedCents = collections
    .filter(
      (collection) =>
        collection &&
        collection.current_amount != null &&
        collection.affects_class_balance !== false
    )
    .reduce(
      (sum, collection) =>
        sum + (moneyToCents(collection.current_amount ?? 0) ?? 0),
      0
    );

  return centsToMoney(
    startBalanceCents + totalCollectedCents - totalPayoutCents
  );
}

export function calcTotalCollected(collections = []) {
  return sumMoney(collections.map((collection) => collection.current_amount ?? 0));
}

export function calcRealizedExpenses(expenses = []) {
  return sumMoney(expenses
    .filter(
      (e) =>
        e.status === "Wypłacone" ||
        e.status === "Zakupione / Nie wypłacone"
    )
    .map((expense) => getExpenseTotal(expense)));
}

export function calcPlannedExpenses(planned = []) {
  return sumMoney(
    getActivePlannedExpenses(planned).map(
      (expense) => getKnownPlannedAmount(expense) ?? 0
    )
  );
}

export function calcToReturn(expenses = []) {
  return sumMoney(expenses
    .filter((e) => e.status === "Zakupione / Nie wypłacone")
    .map((expense) => getExpenseTotal(expense)));
}

export function calcForecast(balance = 0, plannedTotal = 0) {
  const result = Number(balance) - Number(plannedTotal);
  return { sufficient: result >= 0, surplus: result };
}

export function calculateAvailableAfterPlanned({
  currentBalance = 0,
  reimbursementAmount = 0,
  plannedExpensesTotal = 0,
} = {}) {
  return (
    Number(currentBalance ?? 0) -
    Number(reimbursementAmount ?? 0) -
    Number(plannedExpensesTotal ?? 0)
  );
}

export function formatPLN(value) {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
  }).format(Number(value ?? 0));
}

export function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Intl.DateTimeFormat("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(dateStr));
}

export function statusColor(status) {
  switch (status) {
    case "Wypłacone":
      return "text-emerald-400 bg-emerald-400/10";
    case "Zakupione / Nie wypłacone":
      return "text-amber-400 bg-amber-400/10";
    case "Planowane":
      return "text-blue-400 bg-blue-400/10";
    case "Anulowane":
      return "text-red-400 bg-red-400/10";
    default:
      return "text-slate-400 bg-slate-400/10";
  }
}
